import { node } from "~/util/dom";
import { createSummaryActions } from "./actions";
import SummaryClosure from "./closure";
import SummaryModules from "./modules/main";
import { ROUTE_CONST } from "~/route/const";
import { standardDistanceFormat, standardTimeFormat } from "~/util/format";
import { createUserSpeedOnboarding } from "~/route/summary/onboarding-user-speed";
import { timeWithUserSpeed } from "~/util/user-speed";
import MyMapsBox from "~/ui/mymaps/box";
import { getNoteVersion, isPremiumNoteUser } from "~/util/user-note";
import { addListener, removeListener } from "~/lib/signals";
import { createEditPropsBtn } from "~/ui/mymaps/edit-props-btn";
import { createMyMapsSharing } from "~/ui/mymaps/sharing";
import { IMapyBoxItem } from "~/interfaces";
import { getUserTime } from "~/util/mapybox";
import { parseDateToTextCommon, parseDateToTextDetail } from "~/route4/pt/detail/utils";
import { CONFIG } from "~/route/globals";

function updateLikeData(changes: any) {
	const likeData = Mapy.CML.state.route.likeData;

	if (likeData) {
		Object.assign(likeData, changes);
		Mapy.CML.state = {
			route: {
				likeData,
			},
		};
	}
}

export default class RouteSummary {
	_button: HTMLHeadingElement;

	_box: HTMLDivElement;

	_departureInfo: HTMLDivElement;

	_modules: SummaryModules;

	_closure: SummaryClosure;

	_index: number;

	_dom: any;

	_printList: string;

	_mymapsBox: MyMapsBox;

	_data: any;

	constructor(data, routeState, index) {
		this._modules = null;
		this._closure = null;
		this._index = index;
		this._dom = {};
		this._box = node("div", { className: `box alt-${index}` });
		this._departureInfo = node("div", { className: "route-summary__departure-info" });
		this._button = node("h3", { className: `alt-${index}` });
		this._mymapsBox = null;

		this._button.addEventListener("click", () => {
			if (this._button.classList.contains("active")) {
				return;
			}

			Mapy.CML.state = {
				route: {
					index,
				},
			};

			Mapy.getComponent("history").save();
		});

		this._button.addEventListener("mouseenter", () => {
			if (index !== Mapy.CML.state.route.hoverIndex) {
				Mapy.CML.state = {
					route: {
						hoverIndex: index,
					},
				};
			}
		});

		this._button.addEventListener("mouseleave", () => {
			if (index !== Mapy.CML.state.route.index) {
				Mapy.CML.state = {
					route: {
						hoverIndex: Mapy.CML.state.route.index,
					},
				};
			}
		});

		this._printList = addListener(this, "print-page", "_printPage");
		this._data = data;
		this._build(data, routeState);
	}

	destroy() {
		if (this._modules) {
			this._modules.destroy();
		}
		if (this._closure) {
			this._closure.destroy();
		}
		this._modules = null;
		removeListener(this._printList);
		this._box.remove();
		this._mymapsBox?.destroy();
	}

	get box() {
		return this._box;
	}

	get button() {
		return this._button;
	}

	get container() {
		return this._dom.container;
	}

	get departureInfo() {
		return this._departureInfo;
	}

	scrollToViewport() {
		if (Mapy.getComponent("layout").getWidth() === 0) {
			return;
		}

		this._dom.scrollLine.scrollIntoView(false);
	}

	show() {
		if (this._modules) {
			this._modules.show();
		}
	}

	hide() {
		if (this._modules) {
			this._modules.hide();
		}
	}

	setAd(container) {
		this._modules.container.before(container);
	}

	reload() {
		this._modules.reloadState();
	}

	setTime(time) {
		// nastavime info o odjezdu ci odchodu
		const routeState = Mapy.CML.state.route;
		let departureDate = new Date(this._data.departureISO);

		// pokud jde o prijezd a nejde o verejnou dopravu, tak musime prepocitat datum
		if (!routeState.params.departure && routeState.params.type !== ROUTE_CONST.TYPE_PUBT) {
			departureDate = new Date((routeState.params.datetime ? new Date(routeState.params.datetime) : new Date()).getTime() - (time * 1000));
		}

		departureDate.setSeconds(0);
		departureDate.setMilliseconds(0);
		this._departureInfo.textContent = routeState.params.type === ROUTE_CONST.TYPE_PUBT
			? parseDateToTextDetail(departureDate, new Date(this._data.firstTrafficDepartureISO), this._data.walk)
			: parseDateToTextCommon(departureDate, this._data.walk, routeState.params.departure);

		this._button.querySelector(".time").textContent = standardTimeFormat(time).string;
	}

	/**
	 * Vytvoreni summary.
	 */
	_build(data, routeState) {
		Mapy.Util.clear(this._box);

		if (data) {
			const length = data.totalLength;
			const time = data.totalTime;
			const distance = standardDistanceFormat(length);
			let distanceLabel = distance.string;

			if (length > 1000000) { // pokud je trasa delsi jak 1000km, tak zaokrouhlujeme na cela cisla
				distanceLabel = `${Mapy.Util.formatLocaleNumber(Math.round(distance.distanceNumber))}${String.fromCharCode(160)}${distance.unit}`;
			}

			// nastavime info o odjezdu ci odchodu
			const departureDate = new Date(data.departureISO);

			departureDate.setSeconds(0);
			departureDate.setMilliseconds(0);
			this._departureInfo.textContent = routeState.params.type === ROUTE_CONST.TYPE_PUBT
				? parseDateToTextDetail(departureDate, new Date(data.firstTrafficDepartureISO), data.walk)
				: parseDateToTextCommon(departureDate, data.walk, routeState.params.departure);

			this._button.appendChild(node("span", { className: "time", textContent: standardTimeFormat(timeWithUserSpeed(time, routeState.params.criterion)).string }));
			this._button.appendChild(node("span", { className: "distance", textContent: distanceLabel }));
			// trasa autem a rychla - potreba zvyraznit cas, ne vzdalenost
			if (routeState.params.type === ROUTE_CONST.TYPE_CAR && ROUTE_CONST.TYPE_CAR_FAST_CRITERIONS.indexOf(routeState.params.criterion) !== -1) {
				this._button.classList.add("highligh-time");
			}

			this._modules = new SummaryModules(data, routeState, this._index);

			// akce
			this._dom.boxActions = createSummaryActions(data, routeState);
			this._dom.scrollLine = node("div", { className: "scroll-line" });

			if (routeState.likeData) {
				const likeData = {
					...routeState.likeData,
				};
				const likeCont = node("div", { className: "route-like-cont" });
				const div = node("div", { className: "route-like" });
				const titleElem = node("h4", {}, likeData.usertitle || likeData.title);

				div.appendChild(titleElem);
				likeCont.appendChild(div);

				if (likeData.foreign) {
					likeCont.appendChild(createMyMapsSharing({
						data: likeData,
					}));
				} else {
					const date = new Date(getUserTime(likeData));
					const dateElem = node("p", { textContent: new Intl.DateTimeFormat(CONFIG.defaultLang, {
						year: "numeric",
						month: "numeric",
						day: "numeric",
					}).format(date) });
					const editBtn = createEditPropsBtn({
						likeData,
						onSave: (newLikeData: Partial<IMapyBoxItem>) => {
							updateLikeData(newLikeData);
						},
					});

					div.appendChild(dateElem);

					if (!Mapy.CUS.mapybox?.date) {
						dateElem.style.visibility = "hidden";
					}

					div.appendChild(editBtn.container);

					const mymapsBox = new MyMapsBox({
						isPremiumUser: likeData.foreign
							? false
							: isPremiumNoteUser(),
						noteVersion: getNoteVersion(),
						data: likeData,
						folder: {
							id: likeData.parent_id,
							title: likeData.parent_name,
						},
						onSignal: signal => {
							switch (signal.name) {
								case "like-update":
									titleElem.textContent = signal.updateData.usertitle || likeData.title;
									Object.assign(likeData, signal.updateData);
									dateElem.textContent = new Intl.DateTimeFormat(CONFIG.defaultLang, {
										year: "numeric",
										month: "numeric",
										day: "numeric",
									}).format(new Date(getUserTime(signal.updateData)));
									updateLikeData(signal.updateData);
									break;

								case "delete":
									Mapy.CML.state = {
										route: {
											likeData: null,
											likeDataChange: false,
										},
									};

									Mapy.getComponent("dimcurator").setState();
									Mapy.getComponent("history").save(true);
									likeCont.remove();
									this._dom.boxActions.resetSave();
									break;

								case "like-move":
									/* eslint-disable camelcase */
									updateLikeData({
										parent_id: signal.folderId,
										parent_name: signal.folderTitle,
									});
									break;

								default:
							}
						},
					});

					this._mymapsBox = mymapsBox;
					likeCont.append(mymapsBox.container);
				}

				this._box.append(likeCont);
			}

			const isClosure = routeState.items?.filter(item => !!item.closures?.length) || [];
			const isWarning = routeState.items?.filter(item => !!item.warnings?.length) || [];

			// existuji nejake uzavirky nebo varovani po ceste
			if (isClosure.length || isWarning) {
				this._closure = new SummaryClosure(routeState, this._index);
				this._box.appendChild(this._closure.container);
			}

			const onboardingUserSpeed = Mapy.CUS.userweb.userSpeedv2 && createUserSpeedOnboarding(this._index);

			this._box.append(
				this._dom.boxActions.container,
				this._dom.scrollLine,
				onboardingUserSpeed ? onboardingUserSpeed : document.createDocumentFragment(),
				this._modules.container,
			);
		}
	}

	_printPage() {
		this._mymapsBox?.print();
	}
}
